import axios from 'axios';
import { Auth } from 'aws-amplify';

// Same URL resolution + auth interceptor pattern as picksApi.ts. Kept as
// its own client instance so the two services don't share rate-limit /
// retry state, but the auth behaviour and base URL choice are identical.
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ??
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4001');

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10_000,
});

client.interceptors.request.use(async (config) => {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    config.headers = config.headers ?? {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (config.headers as any).Authorization = `Bearer ${token}`;
  } catch {
    // No active session — request goes unauthenticated, server replies 401.
  }
  return config;
});

export type InvitesView = {
  used: number;
  allowance: number;
  remaining: number;
  periodStart: string;
  periodEnd: string;
};

export type ApiAccount = {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  address1?: string;
  address2?: string;
  state?: string;
  zip?: string;
  country?: string;
  createdAt?: string;
  updatedAt?: string;
};

/**
 * Fetch the current user's saved profile. Resolves to `null` if the user
 * has never submitted the MyAccount form (server returns 404). Other
 * non-2xx responses surface as thrown errors for the caller to handle.
 */
export const getAccount = async (): Promise<{
  account: ApiAccount;
  invites: InvitesView;
} | null> => {
  try {
    const res = await client.get<{ account: ApiAccount; invites: InvitesView }>('/api/v1/account');
    return res.data;
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const status = (err as any)?.response?.status;
    if (status === 404) return null;
    throw err;
  }
};

export const consumeInvite = async (email: string): Promise<void> => {
  await client.post('/api/v1/account/invites', { inviteeEmail: email });
};

export type SaveAccountInput = Omit<ApiAccount, 'createdAt' | 'updatedAt'>;

export const saveAccount = async (input: SaveAccountInput): Promise<ApiAccount> => {
  const res = await client.put<{ account: ApiAccount }>('/api/v1/account', input);
  return res.data.account;
};
